'use client';

import { useQuery } from 'convex-helpers/react/cache/hooks';
import { api } from 'convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import {
  useCreateGarden,
  useDeleteGarden,
  useDuplicateGarden,
  useUpdateGardenName,
} from '@api/gardens';
import { useMyGardensStore } from '@shared/store/useMyGardensStore';
import {
  canDeleteGarden,
  getGardenName,
  type Garden,
} from '@modules/my-gardens/business_logic';
import { text as myGardensText } from '@modules/my-gardens/text_business_logic';
import { ComponentType, type FormEvent } from 'react';

export interface MyGardensProps {
  isLoading: boolean;
  isSaving: boolean;
  gardens: Garden[];
  onStartCreating: () => void;
  onStartEditing: (gardenId: Id<'gardens'>, currentName: string) => void;
  onDuplicateGarden: (gardenId: Id<'gardens'>) => void;
  onConfirmDelete: (gardenId: Id<'gardens'>) => void;
  onViewGarden: (gardenId: Id<'gardens'>) => void;
  canDeleteGarden: boolean;
  createDialogOpen: boolean;
  newGardenName: string;
  onNewGardenNameChange: (name: string) => void;
  onCreateSubmit: (e: FormEvent) => void;
  onCreateCancel: () => void;
  editDialogOpen: boolean;
  editName: string;
  onEditNameChange: (name: string) => void;
  onEditSubmit: (e: FormEvent) => void;
  onEditCancel: () => void;
  deleteDialogOpen: boolean;
  gardenToDeleteName: string | undefined;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}

interface MyGardensContainerProps {
  View: ComponentType<MyGardensProps>;
  onNavigate: (path: string) => void;
  onError?: (message: string) => void;
}

export function MyGardensContainer({ View, onNavigate, onError }: MyGardensContainerProps) {
  const gardensQuery = useQuery(api.gardens.queries.getUserGardens, {});

  const createDialogOpen = useMyGardensStore((state) => state.createDialogOpen);
  const newGardenName = useMyGardensStore((state) => state.newGardenName);
  const editDialogOpen = useMyGardensStore((state) => state.editDialogOpen);
  const editName = useMyGardensStore((state) => state.editName);
  const editingGarden = useMyGardensStore((state) => state.editingGarden);
  const deleteDialogOpen = useMyGardensStore((state) => state.deleteDialogOpen);
  const gardenToDelete = useMyGardensStore((state) => state.gardenToDelete);
  const isSaving = useMyGardensStore((state) => state.isSaving);

  const setIsSaving = useMyGardensStore((state) => state.setIsSaving);
  const startCreating = useMyGardensStore((state) => state.startCreating);
  const cancelCreating = useMyGardensStore((state) => state.cancelCreating);
  const setNewGardenName = useMyGardensStore((state) => state.setNewGardenName);
  const startEditing = useMyGardensStore((state) => state.startEditing);
  const cancelEditing = useMyGardensStore((state) => state.cancelEditing);
  const setEditName = useMyGardensStore((state) => state.setEditName);
  const confirmDelete = useMyGardensStore((state) => state.confirmDelete);
  const cancelDelete = useMyGardensStore((state) => state.cancelDelete);

  const createGarden = useCreateGarden();
  const updateGardenName = useUpdateGardenName();
  const deleteGarden = useDeleteGarden();
  const duplicateGarden = useDuplicateGarden();

  const isLoading = gardensQuery === undefined;
  const gardenList = gardensQuery ?? [];
  const gardenToDeleteName = getGardenName(gardenList, gardenToDelete);
  const allowDelete = canDeleteGarden(gardenList);

  const handleCreateSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!newGardenName.trim()) return;

    setIsSaving(true);
    try {
      await createGarden({ name: newGardenName.trim() });
      cancelCreating();
    } catch (error) {
      onError?.(myGardensText.createErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingGarden || !editName.trim()) return;

    setIsSaving(true);
    try {
      await updateGardenName({
        gardenId: editingGarden,
        name: editName.trim(),
      });
      cancelEditing();
    } catch (error) {
      onError?.(myGardensText.renameErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!gardenToDelete) return;

    setIsSaving(true);
    try {
      await deleteGarden({ gardenId: gardenToDelete });
      cancelDelete();
    } catch (error) {
      onError?.(myGardensText.deleteErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = async (gardenId: Id<'gardens'>) => {
    setIsSaving(true);
    try {
      await duplicateGarden({ gardenId });
    } catch (error) {
      onError?.(myGardensText.duplicateErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <View
      isLoading={isLoading}
      isSaving={isSaving}
      gardens={gardenList}
      onStartCreating={startCreating}
      onStartEditing={startEditing}
      onDuplicateGarden={handleDuplicate}
      onConfirmDelete={confirmDelete}
      onViewGarden={(gardenId) => onNavigate(`/gardens/${gardenId}`)}
      canDeleteGarden={allowDelete}
      createDialogOpen={createDialogOpen}
      newGardenName={newGardenName}
      onNewGardenNameChange={setNewGardenName}
      onCreateSubmit={handleCreateSubmit}
      onCreateCancel={cancelCreating}
      editDialogOpen={editDialogOpen}
      editName={editName}
      onEditNameChange={setEditName}
      onEditSubmit={handleEditSubmit}
      onEditCancel={cancelEditing}
      deleteDialogOpen={deleteDialogOpen}
      gardenToDeleteName={gardenToDeleteName}
      onDeleteConfirm={handleDeleteConfirm}
      onDeleteCancel={cancelDelete}
    />
  );
}
